// Auras: a radius around a character for a duration (Twilight Sanctuary,
// Spirit Guardians, a paladin's Aura of Protection). Two triggers start one:
// a Beyond20 feature/spell card in Roll20 chat naming the caster, or the
// feature's limited-use counter (or a spell slot) ticking on the sheet.
// The initiative clock counts them down; the caster's token draws the
// radius as aura 2 while it lasts.
import type { Character, DdbFeatureUse } from "./types";

/** The six abilities, spelled as the sheet spells them. */
export type SaveAbility = "str" | "dex" | "con" | "int" | "wis" | "cha";
export const SAVE_LABEL: Record<SaveAbility, string> = { str: "Strength", dex: "Dexterity", con: "Constitution", int: "Intelligence", wis: "Wisdom", cha: "Charisma" };

/** `save` is the ability the TARGET rolls, when the aura calls for one at all.
 *  Most do not: Twilight Sanctuary grants temporary hit points and asks nothing.
 *  Leaving it unset is how an aura says "no save", which is what decides whether
 *  a save is called for in chat and whether the prompt offers Half and Saved. */
export interface AuraDef { key: string; name: string; match: RegExp; radius: number; rounds: number; save?: SaveAbility; concentration?: boolean; color: string; source?: string; passive?: boolean; subclass?: RegExp; feature?: RegExp; rollUnit?: string; dice?: (c: Character) => string | null; rollAt?: "everyTurn" | "casterTurnEnd"; grants?: { nightVision?: "caster" | number };
  /** A radius that depends on the character: a paladin's auras reach 10 ft, and 30 ft from 18th level. */
  radiusFor?: (c: Character) => number;
  /** A feature that is simply ON once the class has it: the level it arrives at, and the class. */
  alwaysOn?: { cls: RegExp; level: number };
  /** Tracked, not drawn: the ring is NOT painted on the token, so it never
   *  claims one of Roll20's two aura slots. Membership is announced in chat and
   *  shown on the caster's tooltip instead. The passive paladin auras are all
   *  identical 10 ft rings, so drawing them only wastes slots. */
  tracked?: boolean }
export interface ActiveAura { key: string; name: string; radius: number; remaining: number | null; total?: number; concentration?: boolean; color: string; since: string; roll?: number; rollUnit?: string }

/** The line posted to Roll20 chat when an aura's effect lands on someone —
 * worded for the ability, with the number rolled. */
export interface EmoteContext { caster: string; target: string; total: number; halved?: boolean }
const EMOTES: Record<string, { grant?: (x: EmoteContext) => string; damage?: (x: EmoteContext) => string }> = {
  "twilight-sanctuary": { grant: (x) => x.target === x.caster ? `${x.caster} draws on Twilight Sanctuary, gaining ${x.total} temporary hit points.` : `${x.caster} bolsters ${x.target} with Twilight Sanctuary, granting ${x.total} temporary hit points.` },
  "spirit-guardians": { damage: (x) => `${x.target} takes ${x.total} damage from ${x.caster}'s Spirit Guardians${x.halved ? " (saved for half)" : ""}.` },
};
export function auraEmote(def: AuraDef, kind: "grant" | "damage", x: EmoteContext): string {
  const own = EMOTES[def.key]?.[kind];
  if (own) return own(x);
  return kind === "grant" ? `${x.caster}'s ${def.name} grants ${x.target === x.caster ? "them" : x.target} ${x.total}${def.rollUnit ? ` ${def.rollUnit}` : ""}.` : `${x.target} takes ${x.total} damage from ${x.caster}'s ${def.name}${x.halved ? " (saved for half)" : ""}.`;
}

/** "Human · Cleric 13" → 13 (a multiclass line names each class with its level). */
const classLevel = (c: Character, cls: RegExp): number => { const m = String(c.detail || "").match(new RegExp(`${cls.source}\\s+(\\d+)`, "i")); return m ? Number(m[1]) : 0; };

/** A paladin's aura: 10 ft, and 30 ft from 18th level. */
const paladinAura = (c: Character) => (classLevel(c, /paladin/) >= 18 ? 30 : 10);

// Durations in the caster's turns (1 minute = 10). Passive auras have no clock.
// `dice` is what the aura rolls per turn at the table (Twilight Sanctuary's
// temp HP, Spirit Guardians' damage); the DM Screen rolls it on every turn
// change unless the player has claimed the roll for that ability.
export const AURAS: AuraDef[] = [
  { key: "twilight-sanctuary", name: "Twilight Sanctuary", match: /twilight sanctuary/i, radius: 30, rounds: 10, color: "#7b68ee", feature: /channel divinity/i, subclass: /twilight/i, rollUnit: "temp HP", dice: (c) => `1d6+${classLevel(c, /cleric/) || 1}`, rollAt: "casterTurnEnd", grants: { nightVision: "caster" } },
  { key: "spirit-guardians", name: "Spirit Guardians", match: /spirit guardians/i, radius: 15, rounds: 100, save: "wis", concentration: true, color: "#f0e68c", rollUnit: "damage", dice: () => "3d8", rollAt: "everyTurn" },
  { key: "crusaders-mantle", name: "Crusader's Mantle", match: /crusader'?s mantle/i, radius: 30, rounds: 10, concentration: true, color: "#ff8c00" },
  { key: "beacon-of-hope", name: "Beacon of Hope", match: /beacon of hope/i, radius: 30, rounds: 10, concentration: true, color: "#ffd700" },
  { key: "emboldening-bond", name: "Emboldening Bond", match: /emboldening bond/i, radius: 30, rounds: 10, color: "#98fb98" },
  { key: "holy-aura", name: "Holy Aura", match: /holy aura/i, radius: 30, rounds: 10, concentration: true, color: "#fffacd" },
  { key: "circle-of-power", name: "Circle of Power", match: /circle of power/i, radius: 30, rounds: 100, concentration: true, color: "#87cefa" },
  // The paladin auras are PASSIVE: no clock, no card, on from the moment the
  // class grants them. They reach 10 ft and 30 ft from 18th level, which is why
  // the radius is a function of the character rather than a number.
  { key: "aura-of-protection", name: "Aura of Protection", match: /aura of protection/i, radius: 10, radiusFor: paladinAura, alwaysOn: { cls: /paladin/, level: 6 }, rounds: 0, passive: true, tracked: true, color: "#f5deb3", source: "Paladin" },
  { key: "aura-of-courage", name: "Aura of Courage", match: /aura of courage/i, radius: 10, radiusFor: paladinAura, alwaysOn: { cls: /paladin/, level: 10 }, rounds: 0, passive: true, tracked: true, color: "#f5deb3", source: "Paladin" },
  { key: "aura-of-warding", name: "Aura of Warding", match: /aura of warding/i, radius: 10, radiusFor: paladinAura, alwaysOn: { cls: /paladin/, level: 7 }, subclass: /ancients/i, rounds: 0, passive: true, tracked: true, color: "#f5deb3", source: "Paladin (Ancients)" },
  { key: "aura-of-devotion", name: "Aura of Devotion", match: /aura of devotion/i, radius: 10, radiusFor: paladinAura, alwaysOn: { cls: /paladin/, level: 7 }, subclass: /devotion/i, rounds: 0, passive: true, tracked: true, color: "#f5deb3", source: "Paladin (Devotion)" },
  { key: "aura-of-hate", name: "Aura of Hate", match: /aura of hate/i, radius: 10, radiusFor: paladinAura, alwaysOn: { cls: /paladin/, level: 7 }, subclass: /oathbreaker/i, rounds: 0, passive: true, tracked: true, color: "#8b0000", source: "Paladin (Oathbreaker)" },
  { key: "aura-of-conquest", name: "Aura of Conquest", match: /aura of conquest/i, radius: 10, radiusFor: paladinAura, alwaysOn: { cls: /paladin/, level: 7 }, subclass: /conquest/i, rounds: 0, passive: true, tracked: true, color: "#4b0082", source: "Paladin (Conquest)" },
  { key: "aura-of-alacrity", name: "Aura of Alacrity", match: /aura of alacrity/i, radius: 10, radiusFor: paladinAura, alwaysOn: { cls: /paladin/, level: 7 }, subclass: /glory/i, rounds: 0, passive: true, tracked: true, color: "#ffb347", source: "Paladin (Glory)" },
];

/** An aura that is TRACKED, not drawn: see AuraDef.tracked. These never claim a
 *  Roll20 aura slot; their membership is announced in chat and shown on the
 *  caster's tooltip instead (the passive paladin auras — Protection, Courage,
 *  Devotion … — which are all identical 10 ft rings). */
export const auraIsTracked = (key: string): boolean => Boolean(AURAS.find((d) => d.key === key)?.tracked);

/** The line posted to Roll20 chat when `who` enters or leaves `caster`'s tracked
 *  aura(s). Because a tracked aura draws no ring, this line (and the token
 *  tooltip) is how the table learns who is under it. `auras` names every tracked
 *  aura of the caster's that shares the radius — they all do — e.g.
 *  ["Aura of Protection", "Aura of Courage"]. `ally` is whether the mover is a
 *  friend; only allies gain a paladin's buff auras. Return "" to stay silent. */
export function trackedAuraChatLine(caster: string, auras: string[], who: string, dir: "enter" | "leave", ally: boolean): string {
  // Announce everyone, friend or foe: some of these auras act on enemies too
  // (Conquest roots the frightened, Hate feeds the paladin's blows), so "who is
  // in the 10 ft" is worth saying whichever side they are on. The ally note is
  // added only when it is true, and never claims a benefit for a foe.
  const list = auras.length ? auras.map((a) => a.replace(/^Aura of\s+/i, "")).join(", ") : "aura";
  const where = `${caster}'s aura${auras.length === 1 ? "" : "s"} (${list})`;
  if (dir === "enter") return `${who} steps into ${where}${ally ? " — gaining its benefits" : ""}.`;
  return `${who} steps out of ${where}${ally ? " — losing its benefits" : ""}.`;
}

export const auraLabel = (a: ActiveAura) => {
  const base = a.remaining === null ? `${a.name} ${a.radius} ft` : `${a.name} (${a.remaining})`;
  return a.roll === undefined || a.roll === null ? base : `${base} · ${a.roll}${a.rollUnit ? ` ${a.rollUnit}` : ""}`;
};

/** A rolled row on a caster's card names the aura the roll belongs to:
 * Beyond20 posts the dice under the character's name as a row labelled
 * "Channel Divinity: Twilight Sanctuary (1d6 + 13)". */
export function auraRollFromCard(rows: { label: string; total: number | null }[]): { def: AuraDef; total: number } | null {
  for (const row of rows) {
    if (row.total === null || row.total === undefined) continue;
    const def = AURAS.find((a) => a.match.test(row.label));
    if (def) return { def, total: Number(row.total) };
  }
  return null;
}

/** "1d6+13" / "3d8" → a total (crypto-backed so table rolls are not Math.random). */
export function rollDice(formula: string): number {
  let total = 0;
  for (const term of formula.replace(/\s+/g, "").replace(/-/g, "+-").split("+").filter(Boolean)) {
    const m = term.match(/^(-?)(\d*)d(\d+)$/i);
    if (!m) { total += Number(term) || 0; continue; }
    const sign = m[1] === "-" ? -1 : 1, count = Number(m[2] || 1), sides = Number(m[3]);
    const buf = new Uint32Array(count); crypto.getRandomValues(buf);
    for (const v of buf) total += sign * ((v % sides) + 1);
  }
  return total;
}

/** Preference key: this player rolls this ability at the table (the DM Screen
 * then only records what shows up in chat). Lives in layout.auraPlayerRolls. */
export const auraRollKey = (characterId: string, auraKey: string) => `${characterId}:${auraKey}`;

/** A turn passed (from `endedId`): auras rolled every turn get a fresh number
 * for everyone they touch this turn; auras rolled at the end of the caster's
 * turn (Twilight Sanctuary) roll only then and hold the number for the
 * round. Auras a player has claimed are skipped. */
export interface AuraRoll { characterId: string; characterName: string; auraKey: string; aura: string; total: number; unit: string; formula: string }
export function rerollAuras(characters: Character[], playerRolls: Record<string, boolean>, endedId = ""): AuraRoll[] {
  const rolls: AuraRoll[] = [];
  for (const c of characters) for (const a of c.auras || []) {
    const def = AURAS.find((d) => d.key === a.key);
    const formula = def?.dice?.(c);
    if (!def || !formula || playerRolls[auraRollKey(c.id, a.key)]) continue;
    if ((def.rollAt || "everyTurn") === "casterTurnEnd" && c.id !== endedId) continue;
    rolls.push({ characterId: c.id, characterName: c.name, auraKey: a.key, aura: def.name, total: rollDice(formula), unit: def.rollUnit || "", formula });
  }
  return rolls;
}
/** Rolls made by rerollAuras land on the characters (split so the log and the
 * stored state show the same number, whichever render applies it). */
export function applyAuraRolls(characters: Character[], rolls: AuraRoll[]): Character[] {
  if (!rolls.length) return characters;
  return characters.map((c) => {
    const mine = rolls.filter((r) => r.characterId === c.id);
    if (!mine.length) return c;
    return { ...c, auras: (c.auras || []).map((a) => { const r = mine.find((x) => x.auraKey === a.key); return r ? { ...a, roll: r.total, rollUnit: r.unit || undefined } : a; }) };
  });
}

/** The caster rolled the aura's dice again (Twilight Sanctuary's temp HP each
 * turn): the running aura keeps its clock and shows the latest roll. */
export function recordAuraRoll(c: Character, def: AuraDef, total: number): Partial<Character> {
  return { auras: (c.auras || []).map((a) => (a.key === def.key ? { ...a, roll: total, rollUnit: def.rollUnit } : a)) };
}

/** A chat card caption like "Channel Divinity: Twilight Sanctuary (Edam)" → def + caster name. */
export function auraFromCard(caption: string): { def: AuraDef; caster: string } | null {
  const def = AURAS.find((a) => a.match.test(caption));
  if (!def) return null;
  const caster = (caption.match(/\(([^()]+)\)\s*$/)?.[1] || "").trim();
  return { def, caster };
}

/** A limited-use counter that went up (Channel Divinity 1 → 2) → the subclass aura it most likely means. */
export function auraFromFeatureUse(use: DdbFeatureUse, subclasses: string[]): AuraDef | null {
  return AURAS.find((a) => a.feature && a.feature.test(use.name) && (!a.subclass || subclasses.some((s) => a.subclass!.test(s)))) || null;
}

export function startAura(c: Character, def: AuraDef): Partial<Character> {
  const existing = (c.auras || []).filter((a) => a.key !== def.key);
  const timed = !def.passive && def.rounds > 0;
  const aura: ActiveAura = { key: def.key, name: def.name, radius: def.radiusFor ? def.radiusFor(c) : def.radius, remaining: timed ? def.rounds : null, total: timed ? def.rounds : undefined, concentration: def.concentration, color: def.color, since: new Date().toISOString() };
  // Concentration: starting one ends any other concentration aura.
  const kept = def.concentration ? existing.filter((a) => !a.concentration) : existing;
  return { auras: [...kept, aura] };
}
export function endAura(c: Character, key: string): Partial<Character> { return { auras: (c.auras || []).filter((a) => a.key !== key) }; }

/** The caster's turn began: timed auras lose a turn; at 0 they end. */
export function tickAuras(c: Character): { auras: ActiveAura[]; expired: ActiveAura[] } {
  const expired: ActiveAura[] = [];
  const auras = (c.auras || []).flatMap((a) => {
    if (a.remaining === null || a.remaining === undefined) return [a];
    const left = Number(a.remaining) - 1;
    if (left <= 0) { expired.push(a); return []; }
    return [{ ...a, remaining: left }];
  });
  return { auras, expired };
}

/** Previous was clicked: the caster's timed auras get their turn back, up to
 * the spell's full duration (or whatever the DM last set the clock to). */
export function untickAuras(c: Character): ActiveAura[] {
  return (c.auras || []).map((a) => {
    if (a.remaining === null || a.remaining === undefined) return a;
    const cap = Number(a.total || 0) || AURAS.find((d) => d.key === a.key)?.rounds || Infinity;
    return { ...a, remaining: Math.min(cap, Number(a.remaining) + 1) };
  });
}

/** The DM set the clock by hand (time passed between fights, a longer cast):
 * the countdown continues from there, and Previous can back up to it. */
export function setAuraRemaining(c: Character, key: string, remaining: number): Partial<Character> {
  const n = Math.max(0, Math.round(remaining));
  if (n === 0) return endAura(c, key);
  return { auras: (c.auras || []).map((a) => (a.key === key ? { ...a, remaining: n, total: Math.max(Number(a.total || 0), n) } : a)) };
}

/** What the token draws: every active aura, largest first (Roll20 has two
 * aura slots; the connector fits what it can and reports the rest). */
export function tokenAuras(c: Character): { key: string; name: string; radius: number; color: string }[] {
  return [...(c.auras || [])].filter((a) => a.radius > 0 && !auraIsTracked(a.key)).sort((a, b) => b.radius - a.radius).map((a) => ({ key: a.key, name: a.name, radius: a.radius, color: a.color }));
}
/** The colours the DM Screen draws with — a slot holding one of these is ours to reuse. */
export const OWN_AURA_COLORS = [...new Set(AURAS.map((a) => a.color.toLowerCase()))];

/** The auras a character simply HAS: a passive class feature at or above its
 *  level. A paladin does not cast Aura of Protection and there is no card to
 *  notice — it is on, from the moment the class grants it, and the token should
 *  say so without the DM remembering to add it every session.
 *
 *  A SUBCLASS aura needs the oath as well as the level, and the sheet's detail
 *  line ("Human · Paladin 11") does not carry it — D&D Beyond names the
 *  subclass on the character record, which the live poll reports and the
 *  character now keeps. Until that poll has run the oath is unknown, and an
 *  unknown oath grants nothing: guessing which one would be worse than
 *  waiting, since Hate and Devotion do very different things. */
export function alwaysOnAuras(c: Character): AuraDef[] {
  const oaths = (c.subclasses as string[] | undefined) || [];
  return AURAS.filter((d) => {
    if (!d.alwaysOn || classLevel(c, d.alwaysOn.cls) < d.alwaysOn.level) return false;
    return !d.subclass || oaths.some((o) => d.subclass!.test(o));
  });
}